import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Sparkles, Mail, Lock, User, ArrowRight, LogIn } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTheme } from "../theme";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HostAuth() {
  const { isDark } = useTheme();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    if (!isLogin && !name.trim()) {
      toast.error("Please enter your name");
      return;
    }

    setIsLoading(true);
    try {
      const endpoint = isLogin ? "/hosts/login" : "/hosts/register";
      const payload = isLogin
        ? { email, password }
        : { email, password, name };

      const res = await axios.post(`${API}${endpoint}`, payload);

      localStorage.setItem("neuromap_host_token", res.data.access_token);
      localStorage.setItem("neuromap_host_id", res.data.host_id);
      localStorage.setItem("neuromap_host_name", res.data.name);
      localStorage.setItem("neuromap_host_email", res.data.email);

      toast.success(isLogin ? "Welcome back!" : "Account created!");
      navigate("/host/dashboard");
    } catch (err) {
      const msg = err.response?.data?.detail || "Something went wrong";
      toast.error(msg);
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: isDark ? "#0f172a" : "linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #fce7f3 100%)" }} data-testid="host-auth-page">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6" data-testid="back-to-map-link">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-pink-500 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-black text-indigo-600" style={{ fontFamily: "Nunito" }}>Labelled</span>
          </Link>
          <h1 className="text-3xl font-black text-slate-800 mb-2" style={{ fontFamily: "Nunito" }} data-testid="auth-heading">
            {isLogin ? "Event Host Login" : "Create Host Account"}
          </h1>
          <p className="text-slate-500">
            {isLogin ? "Access your event dashboard and analytics" : "Start promoting your neurodivergent community events"}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-slate-200/50 p-8" data-testid="auth-form-card">
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Your Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Smith"
                  className="input-field"
                  data-testid="host-name-input"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="input-field"
                data-testid="host-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="input-field"
                data-testid="host-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full"
              data-testid="host-submit-btn"
            >
              {isLoading ? (
                "Please wait..."
              ) : isLogin ? (
                <>
                  <LogIn className="w-5 h-5" />
                  Sign In
                </>
              ) : (
                <>
                  <ArrowRight className="w-5 h-5" />
                  Create Account
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-indigo-600 hover:text-indigo-700 font-semibold text-sm transition-colors"
              data-testid="toggle-auth-mode-btn"
            >
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
        </div>

        <p className="text-center text-slate-400 text-sm mt-6">
          <Link to="/" className="hover:text-slate-600 transition-colors">Back to Map</Link>
        </p>
      </div>
    </div>
  );
}
